import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { MessageService, TreeNode } from 'primeng/api';
import { catchError, EMPTY, firstValueFrom, Observable, throwError } from 'rxjs';
import { UsersService } from '../../../shared/services/users.service';
import { AssignmentGroupConstants } from '../assignment-group-constants';
import { DropdownValue } from '../models/keyValuePair';
import { AssignmentGroupInteractionService } from '../services/assignment-group-interaction.service';
import { AssignmentGroupService } from '../services/assignment-group.service';
import { AssignmentGroupDetails } from '../models/assignment-group-details';
import { AssignmentGroupSharedDataService } from '../../../../../src/app/shared/services/assignment-group-shared-data.service';
import { UserAssignment } from '../../../../app/shared/models/user-assignment';
import { UserType } from '../../../../app/shared/enums/user-type';

@Component({
  selector: 'app-create-assignment-group',
  templateUrl: './create-assignment-group.component.html',
  styleUrls: ['./create-assignment-group.component.scss'],
})
export class CreateAssignmentGroupComponent implements OnInit {

  @Input() get assignmentGroupToEdit(): any {
    return this._assignmentGroupToEdit;
  }
  set assignmentGroupToEdit(val: AssignmentGroupDetails) {
    if (val) {
      this._assignmentGroupToEdit = val;
      this.setDetailsForEdit();
    }
  }
  private _assignmentGroupToEdit!: AssignmentGroupDetails;
  isEditAssignmentGroupSlider: boolean = false;
  intialUserListToLoad :boolean = false;
  isAssignmentGroupSaveInProgress = false;
  assignmentGroupSaveProgressMessage =
    AssignmentGroupConstants.AssignmentGroup_Save_InProgress;
  treeSelectPlaceHolderText: string = AssignmentGroupConstants.Scope_Tree_Select_PlaceHolder;

  errorMessages: string[] = [];
  userList: any = [];
  eQMSScopes: TreeNode[] = [];
  valueField: string = 'value';
  labelField: string = 'name';
  selectedScope: any = [];
  serviceLookupData: any = {};
  assignments: DropdownValue[] = [];
  toggleSwitchLabel = 'Is Active';
  errorMessageHeaderText: string =
    AssignmentGroupConstants.AssignmentGroup_Creation_Error_Message_Header_Text;

  assignmentGroupForm!: FormGroup;
  mandatoryFields: { [key: string]: boolean } =
    AssignmentGroupConstants.AssignmentGroup_Default_Requiredfields;

    initialEQMSScopeId: any = -1;
    initialAssignment: any = -1;
    hasChanged = false;
    updatedUsersList:any=[];
    validatedInactiveUsers:any=[];
    usersListToValidate:UserAssignment[]=[];
    validatedUsersList:UserAssignment[]=[];
    inactiveGroupsSelected:any;
    isInActiveAssignmentGroupDialog:boolean = false;
    InactiveAssignmentGroupField = '';
    
   
    constructor(
      private _formBuilder: FormBuilder,
      private usersService: UsersService,
      private assignmentGroupInteractionService: AssignmentGroupInteractionService,
      private assignmentGroupService: AssignmentGroupService,
      private messageService: MessageService,
      private assignmentGroupSharedDataService: AssignmentGroupSharedDataService
    )  { }

  ngOnInit(): void {
    this.assignmentGroupForm = this.createAssignmentGroupForm();
    this.initialize();
    this.getSharedData();
    this.assignmentGroupForm?.get('users')?.disable();
  }

  createAssignmentGroupForm() {
    return this._formBuilder.group({
      eQMSScope: new FormControl('', [Validators.required]),
      name: new FormControl('', [Validators.required]),
      description: new FormControl(''),
      assignment: new FormControl('', [Validators.required]),
      users: new FormControl('', [Validators.required]),
      isActive: new FormControl(true),
    });
  }
  setDetailsForEdit = () => {
    this.isEditAssignmentGroupSlider = true;    
    this.intialUserListToLoad = true;
    this.constructEditAssignmentGroup();
  }  

  private constructEditAssignmentGroup() {
      this.assignmentGroupForm.patchValue({
        name: this._assignmentGroupToEdit.name,
        description: this._assignmentGroupToEdit.description,
        assignment: this._assignmentGroupToEdit.assignment,
        isActive: this._assignmentGroupToEdit.isActive,
    });
  }

  initialize() {
    //To reset the form
    this.assignmentGroupInteractionService.closeAndResetAssignmentGroupSlider$.subscribe(
      (isClose) => {
        if (isClose) {
          this.closeAndResetAssignmenTGroupSlider();
        }
      }
    );

    // To load Assignments data
    this.assignmentGroupService
      .getAssignmentGroupLookUpDetails()
      .subscribe((serviceData) => {
        this.serviceLookupData = serviceData;
        this.assignments = this.serviceLookupData.assignments.length
          ? this.serviceLookupData.assignments
          : [];
      });
  }

  //To load the user list 
  async intializeUserList(eQMSScope:any, assignment:number): Promise<Observable<any>> {
    return new Observable((observer) => {
      this.usersService.getUsersForAssignment(eQMSScope, assignment)
        .subscribe(
          async (usersData) => {
            for (let dataItem of usersData) {
              this.userList.push({
                value: dataItem.name,
                name: dataItem.name
              });
            }
            this.assignmentGroupForm?.get('users')?.enable();
            if (!this.intialUserListToLoad) {
              this.assignmentGroupForm.get('users')?.setValue(null);
            }
            await this.processUsersList();
            observer.next();
            observer.complete();
          },
          (error) => {
            observer.error(error);
          }
        );
    });
  }


  async processUsersList(){
      if(!this.isEditAssignmentGroupSlider){
        this.updatedUsersList=[...this.userList];
      }
      else{
        await this.updateUserList();
      }
  }


  async updateUserList() {
    this.updatedUsersList=[...this.userList];
    this.usersListToValidate=[];
    this.usersListToValidate.push({
      userType:UserType.AssignedUser,
      userAssignmentList: this.assignmentGroupToEdit.users
     });

        // Fetch and process the validated users list
    this.validatedUsersList = await firstValueFrom(this.usersService.validateUsers(this.usersListToValidate));

    this.validatedUsersList.forEach(user => {
      user.userAssignmentList.forEach(userInfo => {
        const userItem = { name: userInfo.assignedUserName, value: userInfo.assignedUserName };

        // Check if the item already exists in the target list
        if (!this.updatedUsersList.some((item: { name: string; value: string; }) => item.name === userItem.name && item.value === userItem.value)) {
          this.updatedUsersList.push(userItem);
        }
      });
    });
  }

  onAssignmentOrScopeChange() {
    const eQMSScope = this.assignmentGroupForm.getRawValue().eQMSScope;
    const eQMSScopeId = eQMSScope?.data?.eQMSScopeId;
    const assignment = this.assignmentGroupForm.getRawValue().assignment;
  
    if (eQMSScopeId && assignment) {
      if (!this.initialEQMSScopeId && !this.initialAssignment) {
        this.initialEQMSScopeId = eQMSScopeId;
        this.initialAssignment = assignment;
      }
  
      if (eQMSScopeId !== this.initialEQMSScopeId || assignment !== this.initialAssignment) {
        this.initialEQMSScopeId = eQMSScopeId;
        this.initialAssignment = assignment;
        this.hasChanged = true;
      }
  
      if (this.hasChanged) {
        this.handleValidScopeAndAssignment(eQMSScopeId, assignment);
        this.hasChanged = false; 
      }
    } else {
      this.handleInvalidScopeOrAssignment();
      this.initialEQMSScopeId = -1; this.initialAssignment = -1;
    }
  }

  
  private async handleValidScopeAndAssignment(eQMSScopeId: any, assignment: any) {
    this.userList = [];
    (await this.intializeUserList(eQMSScopeId, assignment)).subscribe(async () => {
      if (this.intialUserListToLoad) {
        await this.assignmentGroupForm.patchValue({ users:this._assignmentGroupToEdit.users.map(userInfo=>userInfo.assignedUserName)});
        this.intialUserListToLoad = false;
      }
    });
  }
  
  private handleInvalidScopeOrAssignment() {
    const usersControl = this.assignmentGroupForm.get('users');
    if (usersControl) {
      usersControl.disable();
    }
  }
  

  closeAndResetAssignmenTGroupSlider() {
    this.assignmentGroupForm.reset();
    this.assignmentGroupForm.get('isActive')!.setValue(true);
    this.assignmentGroupForm.get('description')!.setValue('');
    this.assignmentGroupForm.get('eQMSScope')!.setValue('');

    this.errorMessages = [];
    Object.keys(this.assignmentGroupForm.controls).forEach((key) => {
      this.mandatoryFields[key] = false;
    });
  }

  saveAssignmentGroupForm() {
    this.validateForm();
    if (!this.errorMessages.length) {
      this.postAssignmentGroupForm();
    }
  }

  postAssignmentGroupForm() {
    this.isAssignmentGroupSaveInProgress = true;
    if (this.isEditAssignmentGroupSlider) {
      this.validateInactiveUsersForAssignment();
      
    } else {
      this.saveAssignmentGroup()
    }
  }
  validateInactiveUsersForAssignment(){
    this.inactiveGroupsSelected = undefined;
    this.inactiveGroupsSelected = this.assignmentGroupSharedDataService
    .findInactiveObjectsInValidatedList(this.assignmentGroupForm,this.validatedUsersList, this.userList);
    if(this.inactiveGroupsSelected != undefined && this.inactiveGroupsSelected.length > 0){
      this.isInActiveAssignmentGroupDialog = true;   
      this.InactiveAssignmentGroupField = this.inactiveGroupsSelected;
     }
     else{
          this.updateAssignmentGroup();
     }
  }

  saveAssignmentGroupwithInactiveUsers(){
      this.isInActiveAssignmentGroupDialog = false;
      this.updateAssignmentGroup();
  }

  closeConfirmationDailog(value : boolean){    
    this.isInActiveAssignmentGroupDialog = value
    this.isAssignmentGroupSaveInProgress = false;
  }

  private saveAssignmentGroup = () => {
    let oldScopeValue = this.assignmentGroupForm.getRawValue().eQMSScope;
    if (typeof oldScopeValue != 'number') {
      this.assignmentGroupForm.get('eQMSScope')!.setValue(oldScopeValue.data.eQMSScopeId);
    }
    this.assignmentGroupService.saveAssignmentGroupDetails(this.assignmentGroupForm.getRawValue())
      .pipe(
        catchError((err) => {
          if (err instanceof HttpErrorResponse && err.status === 400 && err.error === AssignmentGroupConstants.AssignmentGroup_Duplication_Error_Message) {
            this.handleDuplicateAssignmentGroupError();
            this.isAssignmentGroupSaveInProgress = false;
            if (typeof oldScopeValue != 'number') {
              this.assignmentGroupForm.get('eQMSScope')!.setValue(oldScopeValue);
            }
            return EMPTY;
          }
          else {
            this.handleGenericError();
          }
          this.isAssignmentGroupSaveInProgress = false;
          this.assignmentGroupInteractionService.closeAndResetAssignmentGroupSlider$.next(true);
          return EMPTY;
        })
      )
      .subscribe({
        next: (resp) => {
          this.messageService.add({
            severity: AssignmentGroupConstants.Severity_Success,
            summary: `${AssignmentGroupConstants.AssignmentGroup_Created_Success}`,
          });
          this.isAssignmentGroupSaveInProgress = false;
          this.assignmentGroupInteractionService.updatePanelListOnCreateOrEdit();
          this.assignmentGroupInteractionService.closeAndResetAssignmentGroupSlider$.next(true);
        },
        error: (err) => {
          this.handleGenericError();
          this.isAssignmentGroupSaveInProgress = false;
          this.assignmentGroupInteractionService.closeAndResetAssignmentGroupSlider$.next(true);
        }
      });
  }
  private updateAssignmentGroup = () => {
    let oldScopeValue = this.assignmentGroupForm.getRawValue().eQMSScope;
    if (typeof oldScopeValue != 'number') {
      this.assignmentGroupForm.get('eQMSScope')!.setValue(oldScopeValue.data.eQMSScopeId);
    }
    this.assignmentGroupService.updateAssignmentGroup(this._assignmentGroupToEdit.assignmentGroupId,
      this.assignmentGroupForm.getRawValue())
      .pipe(
        catchError((err) => {
          if (err instanceof HttpErrorResponse && err.status === 400 && err.error === AssignmentGroupConstants.AssignmentGroup_Duplication_Error_Message) {
            this.handleDuplicateAssignmentGroupError();
            this.isAssignmentGroupSaveInProgress = false;
            if (typeof oldScopeValue != 'number') {
              this.assignmentGroupForm.get('eQMSScope')!.setValue(oldScopeValue);
            }
            return EMPTY;
          }
          else {
            this.handleGenericError();
          }
          this.isAssignmentGroupSaveInProgress = false;
          this.assignmentGroupInteractionService.closeAndResetAssignmentGroupSlider$.next(true);
          return EMPTY;
        })
      )
      .subscribe({
        next: (resp) => {
          if (this.isEditAssignmentGroupSlider) {
            this.messageService.add({
              severity: AssignmentGroupConstants.Severity_Success,
              summary: `${AssignmentGroupConstants.AssignmentGroup_Updated_Success}`,
            });
          } else {
            this.messageService.add({
              severity: AssignmentGroupConstants.Severity_Success,
              summary: `${AssignmentGroupConstants.AssignmentGroup_Created_Success}`,
            });
          }

          this.isAssignmentGroupSaveInProgress = false;
          this.assignmentGroupInteractionService.updatePanelListOnCreateOrEdit();
          this.assignmentGroupInteractionService.closeAndResetAssignmentGroupSlider$.next(true);
          this.isEditAssignmentGroupSlider = !this.isEditAssignmentGroupSlider;
        },
        error: (err) => {
          this.handleGenericError();
          this.isAssignmentGroupSaveInProgress = false;
          this.assignmentGroupInteractionService.closeAndResetAssignmentGroupSlider$.next(true);
        }
      });
  }

  private handleDuplicateAssignmentGroupError() {
    const message = `${AssignmentGroupConstants.AssignmentGroup_Duplication_Error_Message_Text} "${this.assignmentGroupForm.get('name')!.value}"`;

    this.messageService.add({
      severity: AssignmentGroupConstants.Severity_Err,
      summary: message,
      life: 20000,
    });
  }

  private handleGenericError() {
    if (this.isEditAssignmentGroupSlider) {
      this.messageService.add({
        severity: AssignmentGroupConstants.Severity_Err,
        summary: `${AssignmentGroupConstants.AssignmentGroup_Updated_Failed}`,
      });
    } else {
      this.messageService.add({
        severity: AssignmentGroupConstants.Severity_Err,
        summary: `${AssignmentGroupConstants.AssignmentGroup_Creation_Failed}`,
      });
    }
  }


  public validateForm() {
    this.errorMessages = [];
    this.mandatoryFields =
      AssignmentGroupConstants.AssignmentGroup_Default_Requiredfields;
    if (this.assignmentGroupForm.valid) {
      return;
    }
    Object.keys(this.assignmentGroupForm.controls).forEach((key) => {
      if (this.assignmentGroupForm.get(key)?.errors) {
        this.errorMessages.push(this.getErrorMessageText(key));
      } else {
        this.mandatoryFields[key] = false;
      }
    });
  }

  private getErrorMessageText(labelName: string): string {
    const errorLabelMapper: { [key: string]: string } = {
      eQMSScope: AssignmentGroupConstants.AssignmentGroup_Scope_Required,
      name: AssignmentGroupConstants.AssignmentGroup_Name_Required,
      assignment: AssignmentGroupConstants.AssignmentGroup_Assignment_Required,
      users: AssignmentGroupConstants.AssignmentGroup_Users_Required,
    };
    this.mandatoryFields[labelName] = true;
    return errorLabelMapper[labelName];
  }

  getSharedData() {
    this.assignmentGroupService
      .getEQMSScopeForAssignmentGroups()
      .subscribe((list) => {
        this.eQMSScopes = this.assignmentGroupService.getEQMSScopeTreeNodes(list);
      });
    this.assignmentGroupInteractionService.geteQMSSelectedScope$.subscribe(
      (value) => {
        this.selectedScope = value;
        this.assignmentGroupForm.get('eQMSScope')!.setValue(this.selectedScope);
      }
    );
  }
}
